import TeamImage from 'components/TeamImage';
import styles from './styles/PickEmModule.module.scss';
import cn from 'classnames';
import Clickable from '@/components/Clickable';
import CheckCircle from 'icons/CheckCircle';
import { findParticipantByGameTeamId } from 'containers/GameContainer';
import { GameTeamResultEnum } from 'queries';
import LocalizedDate from 'components/LocalizedDate';
import { COMPLETED } from 'constants/GameStatuses';
import Lock from 'icons/Lock';
import useScreenSize from 'hooks/useScreenSize';
import { Fragment } from 'react';

interface PickEmGame {
  date: string;
  statusId: number;
  is_neutral: boolean | undefined;
  gameTeams: {
    id: string;
    isHome: boolean;
    picks: number;
    standing: {
      overallRecord: string;
    };
    team: {
      id: string;
      name: string;
      image?: string;
    };
  }[];
  contest:
    | {
        contestParticipants?: {
          id: string;
          participant:
            | {
                __typename: string;
                id: string;
              }
            | { __typename: string };
        }[];
      }
    | undefined;
}

interface ContestParticipant {
  id: string;
  participant: {
    __typename: string;
    id: string; // this is the same as gameTeam.team.id
  };
  votes: number;
  result?: GameTeamResultEnum; // enum for this?
  score?: number;
}

export default function PickEmModule({
  game,
  totalCheers,
  disableVote,
  pickEmGameTeamId,
  pickTeam,
  homeSeed,
  awaySeed,
  hideSeedNumbers,
  showTieBreaker,
  tieBreakerValue,
  onTiebreakerChange,
  disableInput,
  tieBreakerMessage
}: {
  game: PickEmGame;
  totalCheers: number;
  disableVote: boolean;
  pickEmGameTeamId?: string;
  pickTeam: (gameTeamId: string | null) => void;
  homeSeed?: string;
  awaySeed?: string;
  hideSeedNumbers?: boolean;
  showTieBreaker?: boolean;
  tieBreakerValue?: number | null;
  onTiebreakerChange?: (val?: number) => void;
  disableInput?: boolean;
  tieBreakerMessage?: string;
}) {
  const homeGameTeam = game.gameTeams.find(gameTeam => gameTeam.isHome);
  const awayGameTeam = game.gameTeams.find(gameTeam => !gameTeam.isHome);

  if (!homeGameTeam || !awayGameTeam) {
    return null;
  }

  const homeContestParticipant = game.contest?.contestParticipants?.find(c =>
    findParticipantByGameTeamId(c, homeGameTeam.team.id)
  ) as ContestParticipant;
  const awayContestParticipant = game.contest?.contestParticipants?.find(c =>
    findParticipantByGameTeamId(c, awayGameTeam.team.id)
  ) as ContestParticipant;

  const isGameCompleted = game.statusId === COMPLETED;
  const upcomingLabel = game.is_neutral ? 'VS' : 'AT';

  return (
    <div className={styles.PickEmBlocks}>
      <div className={styles.GameDate}>{isGameCompleted ? 'FINAL' : <LocalizedDate date={game.date} format="ddd, hh:mm a" />}</div>
      <PickEmTeam
        contestParticipant={awayContestParticipant}
        gameTeam={awayGameTeam}
        totalCheers={totalCheers}
        disableVote={disableVote}
        pickEmGameTeamId={pickEmGameTeamId}
        pickTeam={pickTeam}
        showScore
        isGameCompleted={isGameCompleted}
        seed={hideSeedNumbers ? undefined : awaySeed}
      />
      <div className={styles.PickEmAt}>
        <div className={styles.PickEmAtBar}></div>
        <div className={styles.PickEmAtInner}>{disableVote ? <Lock /> : upcomingLabel}</div>
      </div>
      <PickEmTeam
        contestParticipant={homeContestParticipant}
        gameTeam={homeGameTeam}
        totalCheers={totalCheers}
        disableVote={disableVote}
        pickEmGameTeamId={pickEmGameTeamId}
        pickTeam={pickTeam}
        reverse
        showScore
        isGameCompleted={isGameCompleted}
        seed={hideSeedNumbers ? undefined : homeSeed}
      />
      {showTieBreaker && (
        <div className={styles.TieBreakerSection}>
          <div className={styles.TieBreakerContent}>
            TIEBREAKER
            <div className={styles.TieBreakerBottomContent}>
              <div className={styles.TieBreakerDescription}>{tieBreakerMessage}</div>
              <input
                type="number"
                className={styles.TieBreakerInput}
                value={tieBreakerValue ?? ''}
                onChange={e => onTiebreakerChange?.(e.target.value === '' ? undefined : +e.target.value)}
                placeholder="Total Points"
                disabled={disableInput}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface PickEmTeamProps {
  // gameTeam: GamesShowQuery['game']['gameTeams'][number];
  gameTeam: PickEmGame['gameTeams'][number];
  contestParticipant: ContestParticipant;
  totalCheers: number;
  pickTeam: (gameTeamId: string | null) => void;
  disableVote?: boolean;
  pickEmGameTeamId?: string;
  reverse?: boolean;
  showScore?: boolean;
  isGameCompleted: boolean;
  seed?: string;
}

const PickEmTeam = ({
  contestParticipant,
  gameTeam,
  totalCheers,
  disableVote,
  pickEmGameTeamId,
  pickTeam,
  reverse,
  showScore,
  isGameCompleted,
  seed
}: PickEmTeamProps) => {
  const { isSmallScreen } = useScreenSize();
  const score = contestParticipant?.score;
  const result = contestParticipant?.result;
  const gameTeamVotes = contestParticipant?.votes;
  const cheerPercentage = totalCheers === 0 ? 0 : ((gameTeamVotes / totalCheers) * 100).toFixed(0);

  const isSelected = contestParticipant && contestParticipant?.id === pickEmGameTeamId;
  const isCorrectPick = isSelected && result === GameTeamResultEnum.WIN;
  const isIncorrectPick = isSelected && isGameCompleted && !isCorrectPick;

  const checkCircleColor = isCorrectPick ? '#1A952A' : isIncorrectPick ? '#E1464A' : '#5B667C';

  const handleClick = () => {
    if (isSelected) {
      pickTeam(null);
    } else {
      contestParticipant && pickTeam(contestParticipant.id);
    }
  };

  const checkCircle =
    isGameCompleted && isSelected ? (
      <CheckCircle className={styles.PickEmCheck} color={checkCircleColor} width={14} height={14} />
    ) : isSelected ? (
      <CheckCircle className={styles.PickEmCheck} color="#5B667C" width={20} height={20} />
    ) : null;

  const scoreElement =
    showScore && result && score !== undefined ? (
      <div
        className={cn(styles.Score, {
          [styles.Winner]: result === GameTeamResultEnum.WIN,
          [styles.Reverse]: reverse
        })}
      >
        {reverse && checkCircle}
        {score}
        {!reverse && checkCircle}
      </div>
    ) : (
      checkCircle && (
        <div
          className={cn(styles.Score, styles.OnlyCheck, {
            [styles.Reverse]: reverse
          })}
        >
          {checkCircle}
        </div>
      )
    );

  const teamImageElement = (
    <div
      className={cn(styles.PickEmTeamImage, {
        [styles.Reverse]: reverse && isSmallScreen,
        [styles.HasCheckCircle]: score || checkCircle,
        [styles.AlignRight]: reverse && !scoreElement
      })}
    >
      <TeamImage alt={gameTeam.team.name} src={gameTeam.team.image} />
    </div>
  );

  const teamInfoElement = (
    <div
      className={cn(styles.PickEmTeamInfo, {
        [styles.Reverse]: reverse
      })}
    >
      <div
        className={cn(styles.PickEmTeamWrapper, {
          [styles.Reverse]: reverse
        })}
      >
        {seed && <div className={styles.PickEmTeamRank}>{seed}</div>}
        <div
          className={cn(styles.PickEmTeamName, {
            [styles.Reverse]: reverse && !seed
          })}
        >
          {gameTeam.team.name}
        </div>
        <div className={styles.PickEmTeamRecord}>{gameTeam.standing.overallRecord}</div>
      </div>
      <div
        className={cn(styles.PickEmPercentage, {
          [styles.Favorite]: !result && cheerPercentage && Number(cheerPercentage) > 50,
          [styles.Underdog]: !result && cheerPercentage && Number(cheerPercentage) < 50,
          [styles.Reverse]: reverse
        })}
      >
        {cheerPercentage}% Picked
      </div>
    </div>
  );

  const elements =
    reverse && !isSmallScreen
      ? [scoreElement, teamInfoElement, teamImageElement]
      : !isSmallScreen
        ? [teamImageElement, teamInfoElement, scoreElement]
        : [scoreElement, teamImageElement, teamInfoElement];

  return (
    <div
      className={cn(styles.PickEmTeam, {
        [styles.SelectedTeamBlock]: isSelected,
        [styles.CorrectPickBorder]: isSelected && isGameCompleted && isCorrectPick,
        [styles.IncorrectPickBorder]: isSelected && isGameCompleted && isIncorrectPick
      })}
    >
      <Clickable
        className={cn(styles.TeamBlock, {
          [styles.DisabledTeamBlock]: disableVote
        })}
        onClick={handleClick}
        disabled={disableVote}
      >
        {elements.map((element, index) => (
          <Fragment key={index}>{element}</Fragment>
        ))}
      </Clickable>
    </div>
  );
};
